import React from "react";
import { convertFinalCheckLabel } from "@sscs/report/utils";
import { IconButton, Link, LongTextLabel, TextWithStatus } from "@sscs/ui";
import { AddIcon } from "@sscs/ui/icons";
import { AllowedTo, LinkToReport, UserAvatar } from "Components";
import { ORGANIZATION_CATALOG_ID, PERMISSION } from "Constants";
import { useCatalogRoute } from "Hooks";
import { Version } from "Types";
import { useVersionReportSummary } from "../../../hooks";
import styles from "./versionInfo.module.scss";

type ViewReportProps = {
  rowIndex: number;
  version: Version;
};

const ViewReport = ({
  rowIndex,
  version,
}: ViewReportProps): React.JSX.Element | null => {
  const { reportSummary } = useVersionReportSummary(version);
  const { isRepro } = convertFinalCheckLabel(reportSummary?.scan_report.checks);
  const processingCode = isRepro
    ? version.artifacts_summary.repro?.current_status.code
    : version.artifacts_summary.version.current_status.code;

  if (processingCode !== "DONE") {
    return (
      <LongTextLabel
        label={version.software_info.version}
        className={styles.processing}
      />
    );
  }

  return (
    <LinkToReport
      id={`version-${rowIndex}`}
      report={{
        projectName: version.project_name,
        packageName: version.package_name,
        version: version.software_info.version,
        isRepro: isRepro,
      }}
    >
      <LongTextLabel
        label={version.software_info.version}
        className={styles.current}
      />
    </LinkToReport>
  );
};

type VersionInfoProps = {
  rowIndex: number;
  version: Version;
  predecessorVersion: string | undefined;
  inCatalog: boolean;
  attachedToExternalCatalog: boolean;
  onUploadNext: () => void;
};

const VersionInfo = ({
  rowIndex,
  version,
  predecessorVersion,
  inCatalog,
  attachedToExternalCatalog,
  onUploadNext,
}: VersionInfoProps): React.JSX.Element => {
  const catalogRoute = useCatalogRoute({
    catalogId: ORGANIZATION_CATALOG_ID,
    platform: version.software_info.platform,
    packageId: version.package_id,
  });

  return (
    <div className={styles["version-info"]}>
      <UserAvatar user={version.user} size="lg" />
      <div className={styles["version-name"]}>
        <TextWithStatus
          text={
            <div className={styles["version-label"]}>
              <ViewReport rowIndex={rowIndex} version={version} />
              {inCatalog && (
                <Link
                  to={catalogRoute}
                  className={styles["in-catalog"]}
                  underline="hover"
                >
                  In catalog
                </Link>
              )}
            </div>
          }
        >
          {predecessorVersion && (
            <LongTextLabel
              label={`Derived from ${predecessorVersion}`}
              className={styles.predecessor}
            />
          )}
        </TextWithStatus>
      </div>
      {!attachedToExternalCatalog && (
        <AllowedTo permissions={[PERMISSION.UPLOAD_VERSION]}>
          <IconButton
            id={`upload-next-${rowIndex}`}
            onClick={onUploadNext}
            className={styles.upload}
            tooltipTitle="Upload derived version"
            size="small"
          >
            <AddIcon size="md" className={styles.icon} />
          </IconButton>
        </AllowedTo>
      )}
    </div>
  );
};

export default VersionInfo;
